import React, { useEffect, useRef, useState } from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { Input } from '../pilot-ui/input';
import { Label } from '../pilot-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../pilot-ui/select';
import {
  Report,
  ShareScope,
  fetchShareScopes,
  isAbortError,
  reportErrorCode,
  reportErrorStatus,
} from '../../api/deviceListReports';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { DeviceListView } from './deviceListPrefs';
import { SharedContentLine, isScopePath, sharedContentLines } from './reportView';

/**
 * FS-889 — the saved-report dialogs, all on ConfirmDialog: a name box (Save
 * report, Save a copy, Rename), Share / Change sharing, the confirmation
 * before saving to a report other people see, and "someone saved first".
 *
 * Each dialog owns its `submitting` and `error`: the request runs from the
 * dialog, which stays open (and cannot be dismissed) until it answers, and a
 * refusal shows in the banner under the heading. `onSubmit` throws to refuse,
 * and otherwise says what happened: only `done` closes the dialog itself.
 * For every other outcome its owner has already replaced it (`stale` opens
 * the "someone saved first" dialog in its place), closed the report (`gone`)
 * or moved on to another opening (`retired`); closing here as well would
 * wipe what the owner put up.
 *
 * Every dialog takes `returnFocusTo`: the report menu closes before a dialog
 * opens, so the item that was clicked is gone by the time the dialog looks
 * for the element to give focus back to.
 */

/** What a submitted write did. */
export type WriteOutcome = 'done' | 'stale' | 'gone' | 'retired';

export const REPORT_NAME_MAX = 80;

const NAME_TITLE = `The report's name, 1 to ${REPORT_NAME_MAX} characters, as the report menu shows it`;

const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/** Why a typed name cannot be saved yet, or null when it can. */
export function reportNameProblem(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === '') return 'Give the report a name.';
  if (trimmed.length > REPORT_NAME_MAX) return `Names are at most ${REPORT_NAME_MAX} characters.`;
  if (CONTROL_CHARS.test(trimmed)) return 'The name contains a character that cannot be saved.';
  return null;
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Who sees a report shared at `path`: the accounts whose device scope covers
 * it. The root is no exception — an account scoped to /a/ does not cover /,
 * so "everyone with device access" would overstate it.
 */
export function audienceText(path: string, accountCount: number | null): string {
  const who = `every console account whose device scope covers ${path}`;
  if (accountCount === null) return `Shared with ${who}.`;
  return `Shared with ${who}: ${accountCount} ${accountCount === 1 ? 'account can' : 'accounts can'} see this.`;
}

const inputClass = 'w-full';

/** The name, audience, filters and search text a share publishes. */
export const SharedContent: React.FC<{
  name: string;
  audience: string;
  lines: SharedContentLine[];
}> = ({ name, audience, lines }) => (
  <div className="text-sm" data-testid="shared-content">
    <p className="mb-2">{audience}</p>
    <p className="mb-1">They will see exactly this:</p>
    <dl className="max-h-48 overflow-y-auto rounded-sm border bg-gray-50 p-2">
      <div className="flex gap-2">
        <dt className="shrink-0 font-medium text-gray-600">Name</dt>{' '}
        <dd className="wrap-break-word">{name}</dd>
      </div>
      {lines.length === 0 && (
        <div className="text-muted-foreground">No filters and no search text: every device their scope allows.</div>
      )}
      {lines.map((l) => (
        <div key={l.label} className="flex gap-2">
          <dt className="shrink-0 font-medium text-gray-600">{l.label}</dt>{' '}
          <dd className="wrap-break-word">{l.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

// ---------------------------------------------------------------------------
// Name box: Save report, Save a copy, Rename
// ---------------------------------------------------------------------------

export interface ReportNameDialogProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  initialName: string;
  /** The caller's own report names, for the soft duplicate warning. */
  myReportNames: string[];
  /** A name box for an existing report: its own name never warns, and
   *  confirming it unchanged would do nothing (FS-924). */
  currentName?: string;
  /** Private by default: the dialog says so for a new report or a copy. */
  savesPrivate?: boolean;
  returnFocusTo?: React.RefObject<HTMLElement | null> | string | null;
  onSubmit: (name: string) => Promise<WriteOutcome>;
  onClose: () => void;
}

export const ReportNameDialog: React.FC<ReportNameDialogProps> = ({
  open,
  title,
  confirmLabel,
  initialName,
  myReportNames,
  currentName,
  savesPrivate = false,
  returnFocusTo = null,
  onSubmit,
  onClose,
}) => {
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
      setSubmitting(false);
    }
  }, [open, initialName]);

  const problem = reportNameProblem(name);
  const unchanged = !problem && currentName !== undefined && name.trim() === currentName;
  const duplicate =
    !problem &&
    !(currentName !== undefined && sameName(name, currentName)) &&
    myReportNames.some((n) => sameName(n, name));
  const showProblem = !!problem && (name.trim() !== '' || error === problem);

  const submit = async () => {
    if (submitting || unchanged) return;
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if ((await onSubmit(name.trim())) === 'done') onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title={title}
      confirmLabel={confirmLabel}
      confirmTitle={
        unchanged
          ? "This is already the report's name: type a different one to rename it"
          : `${confirmLabel} the report under this name`
      }
      submitting={submitting}
      confirmDisabled={unchanged}
      disabledReason="Nothing to save: the report name has not changed."
      error={error}
      returnFocusTo={returnFocusTo}
      onConfirm={submit}
      onCancel={() => {
        if (!submitting) onClose();
      }}
    >
      <div className="text-sm">
        <Label htmlFor="report-name" className="mb-1">Report name</Label>
        <Input
          id="report-name"
          type="text"
          value={name}
          maxLength={REPORT_NAME_MAX + 20}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
          className={inputClass}
          aria-describedby={`report-name-help${showProblem ? ' report-name-error' : ''}`}
          aria-invalid={showProblem || undefined}
          aria-label="Report name"
          title={NAME_TITLE}
          disabled={submitting}
        />
        <p id="report-name-help" className="mt-1 text-xs text-muted-foreground">{NAME_TITLE}.</p>
      </div>
      {showProblem && <p id="report-name-error" className="mt-1 text-xs text-red-700">{problem}</p>}
      {duplicate && (
        <p className="mt-1 text-xs text-amber-800" data-testid="duplicate-name-warning">
          You already have a report called &ldquo;{name.trim()}&rdquo;. Two reports can share a name; the
          menu will show both.
        </p>
      )}
      {savesPrivate && (
        <p className="mt-2 text-xs text-muted-foreground">
          Saved as a private report: only you see it until you share it from the report menu.
        </p>
      )}
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Share / Change sharing
// ---------------------------------------------------------------------------

const CUSTOM_PATH = '\u0000custom';
const NO_SHARE_PATH = '__none__';
const scopeOptionLabel = (scope: ShareScope) =>
  `${scope.path}${scope.account_count >= 0 ? ` — ${scope.account_count} ${scope.account_count === 1 ? 'account' : 'accounts'} can see this` : ''}`;

export interface ShareReportDialogProps {
  open: boolean;
  report: Report;
  /** The state that will be persisted: the working view when modified, else the saved one. */
  candidate: DeviceListView;
  /** The candidate is the working view: it is saved together with the share. */
  candidateIsWorkingView: boolean;
  /** The share also stores the candidate as the report's state, so it changes the report even at its
   *  current path and name (FS-924). */
  savesState?: boolean;
  facetLabel?: (filterKey: string, value: string) => string | undefined;
  returnFocusTo?: React.RefObject<HTMLElement | null> | string | null;
  onSubmit: (args: { shared_scope: string; name?: string }) => Promise<WriteOutcome>;
  onClose: () => void;
}

export const ShareReportDialog: React.FC<ShareReportDialogProps> = ({
  open,
  report,
  candidate,
  candidateIsWorkingView,
  savesState = false,
  facetLabel,
  returnFocusTo = null,
  onSubmit,
  onClose,
}) => {
  const [scopes, setScopes] = useState<ShareScope[] | null>(null);
  const [scopesError, setScopesError] = useState<string | null>(null);
  const [choice, setChoice] = useState<string>(report.shared_scope ?? '');
  const [customPath, setCustomPath] = useState('');
  const [customCount, setCustomCount] = useState<{ path: string; count: number | null; error: string | null } | null>(null);
  const [name, setName] = useState(report.name);
  const [nameTaken, setNameTaken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Reset and load the paths each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setChoice(report.shared_scope ?? '');
    setCustomPath('');
    setCustomCount(null);
    setName(report.name);
    setNameTaken(false);
    setError(null);
    setSubmitting(false);
    setScopes(null);
    setScopesError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    fetchShareScopes(controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        setScopes(list);
        // A report already shared somewhere stays on its path unless changed.
        if (report.shared_scope && !list.some((s) => s.path === report.shared_scope)) {
          setScopes([...list, { path: report.shared_scope, account_count: -1 }]);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setScopesError(extractErrorMessage(err));
      });
    return () => controller.abort();
  }, [open, report.id, report.name, report.shared_scope]);

  const customValid = isScopePath(customPath);
  const checkCustomPath = async () => {
    if (!customValid) return;
    const controller = new AbortController();
    try {
      const list = await fetchShareScopes(controller.signal, customPath);
      const hit = list.find((s) => s.path === customPath);
      setCustomCount({ path: customPath, count: hit ? hit.account_count : null, error: null });
    } catch (err) {
      if (isAbortError(err)) return;
      const status = reportErrorStatus(err);
      setCustomCount({
        path: customPath,
        count: null,
        error:
          status === 403
            ? 'You cannot share to that path: your own device scope does not cover it.'
            : extractErrorMessage(err),
      });
    }
  };

  const chosenPath = choice === CUSTOM_PATH ? customPath : choice;
  const chosenScope = scopes?.find((scope) => scope.path === choice);
  const customPathProblem = choice === CUSTOM_PATH
    ? !customValid
      ? 'A path starts and ends with a slash, like /residential/pilot/, and is at most 255 characters.'
      : customCount?.path === customPath ? customCount.error : null
    : null;
  const pathProblem = customPathProblem || (chosenPath === '' ? 'Choose a path to share this report at.' : null);
  const pathOk = !pathProblem;
  const chosenCount: number | null =
    choice === CUSTOM_PATH
      ? customCount?.path === customPath
        ? customCount.count
        : null
      : (scopes?.find((s) => s.path === choice)?.account_count ?? null);
  const countOrNull = chosenCount !== null && chosenCount >= 0 ? chosenCount : null;

  const lines = sharedContentLines(candidate, facetLabel);
  const nameProblem = nameTaken ? reportNameProblem(name) : null;
  // FS-924: the report's own path and name, with no state to store, would change nothing.
  const unchanged =
    pathOk &&
    !nameProblem &&
    !savesState &&
    chosenPath === report.shared_scope &&
    (!nameTaken || name.trim() === report.name);

  const submit = async () => {
    if (submitting || unchanged) return;
    if (!pathOk || nameProblem) {
      setError(nameProblem || pathProblem);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const outcome = await onSubmit({ shared_scope: chosenPath, ...(nameTaken ? { name: name.trim() } : {}) });
      if (outcome === 'done') onClose();
    } catch (err) {
      const code = reportErrorCode(err);
      if (code === 'name_taken') {
        setNameTaken(true);
        setError(`A report called "${name.trim()}" already exists at ${chosenPath}. Give this one another name.`);
      } else {
        setError(extractErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const changing = report.shared_scope !== null;
  return (
    <ConfirmDialog
      open={open}
      title={changing ? 'Change sharing' : 'Share report'}
      confirmLabel={changing ? 'Change sharing' : 'Share'}
      confirmTitle={
        unchanged
          ? `The report is already shared at ${chosenPath} under this name: choose another path to change its sharing`
          : changing
            ? 'Move the report to the chosen path; the accounts it covers see it from now on'
            : 'Share the report at the chosen path so the accounts it covers can open it'
      }
      submitting={submitting}
      confirmDisabled={scopes === null || unchanged}
      disabledReason={scopesError
        ? 'The share paths could not be loaded. Close this dialog and try again.'
        : scopes === null ? 'Loading the paths you can share to.' : 'Nothing to save: sharing has not changed.'}
      error={error}
      deferredInitialFocus="#report-share-path"
      returnFocusTo={returnFocusTo}
      onConfirm={submit}
      onCancel={() => {
        if (!submitting) onClose();
      }}
    >
      <div className="space-y-3 text-sm">
        <div>
          <Label htmlFor="report-share-path" className="mb-1">Share at</Label>
          {scopesError ? (
            <p className="text-red-700" role="alert">
              Could not load the paths you can share to: {scopesError}
            </p>
          ) : (
            <Select value={choice || NO_SHARE_PATH} onValueChange={(next) => {
              setChoice(next === NO_SHARE_PATH ? '' : next ?? '');
              setError(null);
            }} disabled={submitting || scopes === null}>
              <SelectTrigger
                id="report-share-path"
                className="w-full"
                aria-label="Share path"
                aria-describedby="report-share-path-help"
                title="Choose the path whose covered accounts can open this report"
              >
                <SelectValue>
                  {scopes === null ? 'Loading paths…' : choice === '' ? 'Choose a path' : choice === CUSTOM_PATH
                    ? 'Custom path…' : chosenScope ? scopeOptionLabel(chosenScope) : choice}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SHARE_PATH}>Choose a path</SelectItem>
                {(scopes ?? []).map((scope) => (
                  <SelectItem key={scope.path} value={scope.path}>
                    {scopeOptionLabel(scope)}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_PATH}>Custom path…</SelectItem>
              </SelectContent>
            </Select>
          )}
          <p id="report-share-path-help" className="mt-1 text-xs text-muted-foreground">Every account whose device scope covers the path can open the shared report.</p>
        </div>
        {choice === CUSTOM_PATH && (
          <div>
            <Label htmlFor="report-custom-share-path" className="mb-1">Custom share path</Label>
            <Input
              id="report-custom-share-path"
              type="text"
              value={customPath}
              onChange={(e) => {
                setCustomPath(e.target.value);
                setError(null);
              }}
              onBlur={() => void checkCustomPath()}
              placeholder="/residential/pilot/"
              className={`${inputClass} font-mono`}
              aria-label="Custom share path"
              aria-describedby={`report-custom-share-path-help${customPathProblem ? ' report-custom-share-path-error' : ''}`}
              aria-invalid={!!customPathProblem || undefined}
              title="A scope path, starting and ending with a slash. Leaving the box checks that your own device scope covers it and counts who can see it."
              disabled={submitting}
            />
            <p id="report-custom-share-path-help" className="mt-1 text-xs text-muted-foreground">Start and end with a slash; the path must be inside your device scope.</p>
            {customPathProblem && <p id="report-custom-share-path-error" className="mt-1 text-xs text-red-700" role={customValid ? 'alert' : undefined}>{customPathProblem}</p>}
            {customValid && customCount?.path === customPath && !customCount.error && customCount.count !== null && (
              <p className="mt-1 text-xs text-gray-600">
                {customCount.count} {customCount.count === 1 ? 'account' : 'accounts'} can see this.
              </p>
            )}
          </div>
        )}
        {nameTaken && (
          <div>
            <Label htmlFor="report-share-name" className="mb-1">Report name</Label>
            <Input
              id="report-share-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              className={inputClass}
              aria-label="Report name"
              aria-describedby={`report-share-name-help${nameProblem ? ' report-share-name-error' : ''}`}
              aria-invalid={!!nameProblem || undefined}
              title={`Another name for the report at this path; names are unique per share path. ${NAME_TITLE}`}
              disabled={submitting}
            />
            <p id="report-share-name-help" className="mt-1 text-xs text-muted-foreground">{NAME_TITLE}.</p>
            {nameProblem && <p id="report-share-name-error" className="mt-1 text-xs text-red-700">{nameProblem}</p>}
          </div>
        )}
        {candidateIsWorkingView && (
          <p className="text-xs text-amber-800" data-testid="share-saves-working-view">
            The report has unsaved changes. Sharing saves them: what you see now is what is shared.
          </p>
        )}
        {pathOk && (
          <SharedContent name={nameTaken ? name.trim() : report.name} audience={audienceText(chosenPath, countOrNull)} lines={lines} />
        )}
      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Save on a shared report: show what everyone at the path will get
// ---------------------------------------------------------------------------

export interface SaveSharedReportDialogProps {
  open: boolean;
  report: Report;
  view: DeviceListView;
  facetLabel?: (filterKey: string, value: string) => string | undefined;
  returnFocusTo?: React.RefObject<HTMLElement | null> | string | null;
  onSubmit: () => Promise<WriteOutcome>;
  onClose: () => void;
}

export const SaveSharedReportDialog: React.FC<SaveSharedReportDialogProps> = ({
  open,
  report,
  view,
  facetLabel,
  returnFocusTo = null,
  onSubmit,
  onClose,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const path = report.shared_scope ?? '/';

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setCount(null);
    const controller = new AbortController();
    fetchShareScopes(controller.signal, path)
      .then((list) => {
        if (controller.signal.aborted) return;
        setCount(list.find((s) => s.path === path)?.account_count ?? null);
      })
      .catch(() => {
        /* the path alone is enough for the confirmation */
      });
    return () => controller.abort();
  }, [open, path]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if ((await onSubmit()) === 'done') onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title={`Save "${report.name}" for everyone?`}
      confirmLabel="Save"
      confirmTitle="Save these changes to the shared report; everyone at the path sees the new version"
      submitting={submitting}
      error={error}
      returnFocusTo={returnFocusTo}
      onConfirm={submit}
      onCancel={() => {
        if (!submitting) onClose();
      }}
    >
      <SharedContent
        name={report.name}
        audience={audienceText(path, count)}
        lines={sharedContentLines(view, facetLabel)}
      />
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Someone saved first (409 stale)
// ---------------------------------------------------------------------------

export const StaleReportDialog: React.FC<{
  open: boolean;
  reportName: string;
  returnFocusTo?: React.RefObject<HTMLElement | null> | string | null;
  onReload: () => void;
  onKeepEditing: () => void;
}> = ({ open, reportName, returnFocusTo = null, onReload, onKeepEditing }) => (
  <ConfirmDialog
    open={open}
    title="Someone saved this report first"
    message={`"${reportName}" changed on the server after you opened it. Reload the saved version and lose the changes on screen, or keep editing and save your copy as a new report.`}
    confirmLabel="Reload the saved version"
    confirmTitle="Replace the view on screen with the version on the server"
    cancelLabel="Keep editing"
    returnFocusTo={returnFocusTo}
    onConfirm={onReload}
    onCancel={onKeepEditing}
  />
);
